import React from 'react';
import { useDrag, DragSourceMonitor } from 'react-dnd';
import { Player } from '../types';

interface DraggableCounterProps {
  id: number;
  placed: boolean;
  onReturn: (id: number) => void;
  player?: Player;
}

const POSITION_COLORS: Record<string, string> = {
  'Lineman': '#64748b', // Slate Grey
  'Blitzer': '#dc2626', // Crimson Red
  'Thrower': '#2563eb', // Sapphire Blue
  'Catcher': '#f97316', // Warm Orange
  'Runner': '#fbbf24',  // Sunny Yellow
  'Blocker': '#16a34a', // Emerald Green
  'Big Guy': '#7c3aed', // Royal Purple
  'Special': '#0d9488', // Premium Dark Teal
};

const POSITION_ABBREVS: Record<string, string> = {
  'Lineman': 'LM',
  'Blitzer': 'BL',
  'Thrower': 'TH',
  'Catcher': 'CA',
  'Runner': 'RN',
  'Blocker': 'BK',
  'Big Guy': 'BG',
  'Special': 'SP',
};

function getArchetype(position: string): string {
  const norm = (position || '').toLowerCase();
  if (norm.includes('lineman')) return 'Lineman';
  if (norm.includes('blitzer')) return 'Blitzer';
  if (norm.includes('thrower')) return 'Thrower';
  if (norm.includes('catcher')) return 'Catcher';
  if (norm.includes('runner')) return 'Runner';
  if (norm.includes('blocker')) return 'Blocker';
  if (
    norm.includes('big guy') || 
    norm.includes('troll') || 
    norm.includes('ogre') || 
    norm.includes('minotaur') || 
    norm.includes('treeman') || 
    norm.includes('kroxigor') || 
    norm.includes('yhetee') || 
    norm.includes('mummy')
  ) {
    return 'Big Guy';
  }
  return 'Special';
}

const ItemTypes = { COUNTER: 'counter' as const };

export const DraggableCounter: React.FC<DraggableCounterProps> = ({
  id,
  placed,
  onReturn,
  player,
}) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.COUNTER,
      item: { id },
      canDrag: () => !placed,
      end: (_, monitor: DragSourceMonitor) => {
        if (!monitor.didDrop()) {
          onReturn(id);
        }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [id, placed]
  );

  const archetype = player ? getArchetype(player.position) : 'Lineman';
  const tokenColor = POSITION_COLORS[archetype] || POSITION_COLORS['Lineman'];
  const tokenAbbrev = POSITION_ABBREVS[archetype] || 'LM';

  return (
    <div
      ref={drag}
      className="counter"
      style={{
        opacity: isDragging ? 0.5 : 1,
        borderRadius: '50%', // Ensure circular shape
        backgroundColor: tokenColor,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '30px',
        height: '30px',
        color: '#ffffff',
        border: '1.5px solid rgba(255, 255, 255, 0.8)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        cursor: 'grab'
      }}
    >
      <span style={{ fontSize: '10px', fontWeight: 700, lineHeight: 1 }}>{id}</span>
      <span style={{ fontSize: '7px', fontWeight: 500, opacity: 0.9, marginTop: '1px' }}>{tokenAbbrev}</span>
    </div>
  );
};